import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AuthLayout } from "@/components/auth/AuthLayout"
import { PasswordInput } from "@/components/auth/PasswordInput"
import { FormAlert } from "@/components/auth/FormAlert"
import { authService } from "@/services/authService"
import { useAuthStore } from "@/store/authStore"
import { afterLoginPath } from "@/lib/pendingInvite"

const apiError = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { error?: string } } }).response?.data?.error || fallback

export function LoginPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [form, setForm] = useState({ email: "", password: "" })
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  // Verificação em duas etapas
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null)
  const [totpCode, setTotpCode] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      const res = await authService.login(form)

      if (res.requiresTwoFactor && res.twoFactorToken) {
        setTwoFactorToken(res.twoFactorToken)
        return
      }

      setAuth(res.user!, res.accessToken)
      navigate(afterLoginPath())
    } catch (err: unknown) {
      setError(apiError(err, "Não foi possível entrar. Tente novamente."))
    } finally {
      setLoading(false)
    }
  }

  const handleVerify2Fa = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      const res = await authService.verifyTwoFactor(twoFactorToken!, totpCode)
      setAuth(res.user!, res.accessToken)
      navigate(afterLoginPath())
    } catch (err: unknown) {
      setError(apiError(err, "Código inválido."))
    } finally {
      setLoading(false)
    }
  }

  if (twoFactorToken) {
    return (
      <AuthLayout
        pageTitle="Verificação em duas etapas"
        title="Verificação em duas etapas"
        description="Digite o código de 6 dígitos que aparece no seu aplicativo autenticador."
      >
        <form onSubmit={handleVerify2Fa} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="totp">Código do autenticador</Label>
            <Input
              id="totp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={totpCode}
              // Filtra antes de limitar: autenticadores exibem "123 456" e o código colado tem 7 caracteres
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="h-12 text-center text-[22px] tracking-[0.5em] num"
              autoFocus
              required
            />
          </div>

          {error && <FormAlert>{error}</FormAlert>}

          <Button type="submit" size="lg" className="w-full" disabled={loading || totpCode.length !== 6}>
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Verificando...</> : "Verificar"}
          </Button>

          <button
            type="button"
            onClick={() => { setTwoFactorToken(null); setTotpCode(""); setError("") }}
            className="w-full text-[14px] text-muted-foreground hover:text-foreground"
          >
            Voltar ao login
          </button>
        </form>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      pageTitle="Entrar"
      title="Entre na sua conta"
      description="Bom ter você de volta."
      footer={
        <p>
          Ainda não tem conta?{" "}
          <Link to="/register" className="font-medium text-foreground underline underline-offset-2">Criar conta grátis</Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="voce@exemplo.com"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            required
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Senha</Label>
            <Link to="/forgot-password" className="text-[13px] text-muted-foreground hover:text-foreground">
              Esqueceu sua senha?
            </Link>
          </div>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            required
          />
        </div>

        {error && <FormAlert>{error}</FormAlert>}

        <Button type="submit" size="lg" className="w-full" disabled={loading}>
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Entrando...</> : "Entrar"}
        </Button>
      </form>
    </AuthLayout>
  )
}
